import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { projectService } from '../services';
import { resolveMediaUrl } from '../services/api';
import PageBreadcrumb from '../components/PageBreadcrumb';

const isDonationEligible = (project) => {
    if (!project) return false;
    if (project.status === 'ongoing') return true;
    if (project.status === 'pending') return true;
    if (project.start_date) {
        return new Date(project.start_date) > new Date();
    }
    return false;
};

const ProjectDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const response = await projectService.getById(id);
                setProject(response.data);
            } catch (error) {
                console.error('Error fetching project details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [id]);

    const canDonate = useMemo(() => isDonationEligible(project), [project]);

    if (loading) {
        return (
            <div className="py-20 text-center">
                <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-20 text-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">Project not found</h1>
                <Link to="/projects" className="text-emerald-600 font-semibold">Back to Projects</Link>
            </div>
        );
    }

    return (
        <div className="bg-gray-50">
            <PageBreadcrumb
                title={project.title}
                description={project.location}
                items={[{ label: 'Projects', to: '/projects' }, { label: project.title }]}
            />

            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="rounded-lg bg-white shadow-lg overflow-hidden">
                        {project.featured_image && (
                            <img
                                src={resolveMediaUrl(project.featured_image)}
                                alt={project.title}
                                className="w-full h-80 object-cover"
                            />
                        )}
                        <div className="p-8">
                            <div className="mb-6 flex flex-wrap items-center gap-4">
                                <span className="rounded-full bg-emerald-100 px-4 py-1 text-emerald-700 font-semibold">
                                    {project.status}
                                </span>
                                <span className="text-gray-600">Cost: PKR {Number(project.cost || 0).toLocaleString()}</span>
                                {project.start_date && <span className="text-gray-600">Start: {new Date(project.start_date).toLocaleDateString()}</span>}
                            </div>
                            <p className="text-gray-700 leading-7 whitespace-pre-line">{project.description}</p>

                            <div className="mt-8 flex flex-wrap gap-4">
                                <button
                                    onClick={() => navigate('/donate', { state: { project } })}
                                    disabled={!canDonate}
                                    className="rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    {canDonate ? 'Donate to This Project' : 'Donation Closed'}
                                </button>
                                <Link to="/projects" className="rounded-lg border border-gray-300 px-6 py-3 font-semibold text-gray-700 hover:bg-gray-100">
                                    Back to Projects
                                </Link>
                            </div>
                            {!canDonate && (
                                <p className="mt-3 text-sm text-gray-500">
                                    Donations are allowed only for ongoing or upcoming projects.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProjectDetails;
